import type { QueryClient } from "@tanstack/react-query"

import { queryKeys } from "@/lib/query-keys"

/**
 * Coalesced refetch of ONE session list (issue #3323 item C2).
 *
 * A single Explorer tab mounts `useSessionsQuery` 5–7 times for the very same
 * list (sidebar, page, file refs, project lookup, …). When every instance
 * reacted to a cross-tab hint on its own, one hint became 5–7
 * `invalidateQueries` calls; each one cancelled the previous refetch and
 * started a new `list-sessions-batch` request, so a reply streaming in
 * another tab turned into a request flood.
 *
 * Every hint-driven refetch therefore goes through here. Calls for the same
 * query client and list key collapse into one `invalidateQueries`, fired once
 * the window closes. The window does NOT reset on later calls: the first call
 * of a burst schedules the refetch and the rest are absorbed, so a steady
 * stream of hints refreshes at a fixed cadence (at most one refetch per
 * window) instead of starving — well inside the "about a second" bar for
 * create, rename and delete.
 */
export const SESSION_LIST_REFETCH_COALESCE_MS = 250

const pendingByClient = new WeakMap<QueryClient, Map<string, ReturnType<typeof setTimeout>>>()
/** Every pending timer, with the cleanup that forgets it — only for the test reset. */
const allTimers = new Map<ReturnType<typeof setTimeout>, () => void>()

/**
 * Schedule a refetch of the session list for `sessionType` / `projectId`.
 * Returns `true` when this call scheduled the refetch, `false` when it was
 * absorbed into one that is already pending.
 */
export function scheduleSessionListRefetch(
  queryClient: QueryClient,
  sessionType: string,
  projectId?: string | null
): boolean {
  const queryKey = queryKeys.sessionHistory(sessionType, projectId)
  const hash = JSON.stringify(queryKey)
  let pending = pendingByClient.get(queryClient)
  if (!pending) {
    pending = new Map()
    pendingByClient.set(queryClient, pending)
  }
  if (pending.has(hash)) return false

  const pendingForClient = pending
  const forget = () => {
    pendingForClient.delete(hash)
    allTimers.delete(timer)
  }
  const timer = setTimeout(() => {
    forget()
    void queryClient.invalidateQueries({ queryKey })
  }, SESSION_LIST_REFETCH_COALESCE_MS)
  pending.set(hash, timer)
  allTimers.set(timer, forget)
  return true
}

/** Test-only: cancel every pending refetch so suites cannot leak into each other. */
export function resetSessionListRefetchScheduler(): void {
  for (const [timer, forget] of [...allTimers]) {
    clearTimeout(timer)
    forget()
  }
}
